/**
 * Date-only helpers — single source of truth for YYYY-MM-DD fields
 * (date_of_birth, date_of_injury, date_of_accident, …).
 *
 * WHY this file exists (2026-08-20 production audit): both patient
 * cards did `new Date("YYYY-MM-DD")` on render, which parses as UTC
 * midnight and formats in the browser's Pacific timezone → renders −1
 * day. On save, some paths round-tripped the picked date through
 * `.toISOString()` which shifted +1 for edits after 5 PM PT. The two
 * shifts cancelled visually so the UI looked self-consistent while
 * stored values drifted every edit. Fax/paperwork generators printing
 * raw storage exposed the drift.
 *
 * CONTRACT: date-only fields travel as plain YYYY-MM-DD strings end-
 * to-end. This module does the ONLY formatting/parsing that touches
 * them. Neither function ever calls `new Date("YYYY-MM-DD")` and no
 * function here calls `.toISOString()`.
 *
 * NOT for timestamp fields. created_at / stage timestamps are true
 * instants whose UTC↔local conversion is correct. `appointment_datetime`
 * is NEITHER: it is Pacific wall clock stored as UTC and must never be
 * converted — see lib/wallClock.js.
 */

const _NULL_LABEL = "—";

// Extract "YYYY-MM-DD" from any of:
//   "2024-07-26"                          → "2024-07-26"
//   "2024-07-26T00:00:00"                 → "2024-07-26"
//   "2024-07-26T00:00:00.000Z"            → "2024-07-26"
//   "2024-07-26T00:00:00.000000+00:00"    → "2024-07-26"
//   Date instance                         → "YYYY-MM-DD" using LOCAL parts
// Returns null when nothing sensible can be extracted.
export function toDateOnlyString(value) {
  if (value === null || value === undefined || value === "") return null;
  // A Date instance means we're at a datepicker boundary — format
  // using LOCAL parts (never UTC / never toISOString), so a Date
  // constructed at midnight local doesn't drift.
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value);
  // Strict YYYY-MM-DD prefix — the ONLY safe extraction from any
  // stored form. Everything after the 10-char prefix is discarded.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

// Render as MM/DD/YYYY without ever touching new Date() on the
// input value. Pure string reordering keeps the render frame-neutral.
// `nullLabel` lets callers pick between "—" (DOB style) and "N/A".
export function formatDateOnly(value, { nullLabel = _NULL_LABEL } = {}) {
  const iso = toDateOnlyString(value);
  if (!iso) return nullLabel;
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
}
